import React from 'react';
import { useTranslation } from 'react-i18next';

interface TrustBadgeProps {
  variant: 'compact' | 'detailed';
}

/** Stripe "S" brand mark — the recognizable gradient shield */
const StripeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-label="Stripe">
    <rect width="32" height="32" rx="6" fill="#635BFF" />
    <path
      d="M15.2 11.2c0-.9.7-1.2 1.9-1.2 1.7 0 3.8.5 5.5 1.4V7.1c-1.8-.7-3.7-1-5.5-1-4.5 0-7.5 2.3-7.5 6.2 0 6.1 8.4 5.1 8.4 7.7 0 1.1-.9 1.4-2.2 1.4-1.9 0-4.4-.8-6.3-1.9v4.3c2.1.9 4.3 1.3 6.3 1.3 4.6 0 7.8-2.3 7.8-6.2-.1-6.5-8.4-5.4-8.4-7.7z"
      fill="#fff"
    />
  </svg>
);

const LockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-.5V4.5A3.5 3.5 0 0 0 8 1zm2 6V4.5a2 2 0 1 0-4 0V7h4z" />
  </svg>
);

const ShieldIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 .5l6.5 2v5c0 3.5-2.7 6.5-6.5 8-3.8-1.5-6.5-4.5-6.5-8v-5L8 .5zm0 1.13L2.5 3.5v4c0 2.9 2.2 5.5 5.5 6.9 3.3-1.4 5.5-4 5.5-6.9v-4L8 1.63z" />
    <path d="M7.25 9.44l-1.72-1.72-.78.78 2.5 2.5 4-4-.78-.78L7.25 9.44z" />
  </svg>
);

const TrustBadge: React.FC<TrustBadgeProps> = ({ variant }) => {
  const { t } = useTranslation();

  if (variant === 'compact') {
    return (
      <div className="flex items-center justify-center gap-2 mt-3">
        <LockIcon className="w-3.5 h-3.5 text-brand-gray" />
        <span className="text-xs text-brand-gray">{t('trust.poweredByStripe')}</span>
        <StripeIcon className="w-5 h-5" />
      </div>
    );
  }

  // detailed variant — inline layout with Stripe branding
  return (
    <div className="bg-white dark:bg-brand-dark border border-gray-100 dark:border-brand-charcoal rounded-lg p-5">
      <div className="flex items-start gap-4">
        <StripeIcon className="w-10 h-10 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
            <h3 className="text-sm font-semibold text-brand-dark dark:text-brand-white">
              {t('trust.secureCheckoutTitle')}
            </h3>
          </div>
          <p className="text-xs text-brand-gray leading-relaxed mb-3">
            {t('trust.secureCheckoutDescription')}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            <span className="flex items-center gap-1.5 text-xs text-brand-gray">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
              {t('trust.encrypted')}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-brand-gray">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
              {t('trust.pciCompliant')}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-brand-gray">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
              {t('trust.noCardStored')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustBadge;
